import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, selectPermissions, selectIsSuperAdmin, selectUser, selectToken } from '../store/slices/authSlice';
import { fetchProfile } from '../store/thunks/authThunks';
import { canAccessModule } from '../../shared/utils/rbac';

/**
 * RouteGuard — protects routes requiring authentication.
 * Pass `module` prop to additionally enforce module-level read access.
 */
const RouteGuard = ({ children, module }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const permissions = useSelector(selectPermissions);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const hasToken = localStorage.getItem('auth_token') || sessionStorage.getItem('isLoggedIn');

  useEffect(() => {
    if (token && (!user || permissions === null)) {
      dispatch(fetchProfile());
    }
  }, [token, user, permissions, dispatch]);

  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for profile permissions before rendering module-protected pages.
  if (module && !isSuperAdmin && permissions === null) {
    return null;
  }

  // Module-level access check (skip if no module specified or user is super admin)
  if (module && !isSuperAdmin) {
    const allowed = canAccessModule(isSuperAdmin, permissions, module);
    if (!allowed) {
      return <Navigate to="/admin/403" replace />;
    }
  }

  return children;
};

/**
 * PublicRouteGuard — redirects authenticated users away from login/signup.
 */
export const PublicRouteGuard = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const hasToken = localStorage.getItem('auth_token') || sessionStorage.getItem('isLoggedIn');

  if (isAuthenticated || hasToken) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

export default RouteGuard;
